import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonActivity, DonationItem, ENVIRONMENT, Environment, Lead, PageActivity, sizeDescriptions } from '@domains';
import { InfoDialogComponent } from '@donor/components/info-dialog/info-dialog.component';
import { ItemRemoveDialogComponent } from '@donor/components/item-remove-dialog/item-remove-dialog.component';
import { ItemSearchDialogComponent } from '@donor/components/item-search-dialog/item-search-dialog.component';
import { PageFooterComponent } from '@donor/components/page-footer/page-footer.component';
import {
  DesignService,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  ResponsiveService,
} from '@rspl-ui';
import { LaddaModule } from 'angular2-ladda';
import { debounce } from 'lodash';
import { take } from 'rxjs';

import { AppService } from '../../../../app.service';
import { CreateProgressComponent } from '../../../../components/create-progress/create-progress.component';
import { OptimizelyService } from '../../../../optimizely.service';
import { FlowService } from '../../donor-flow.service';
import { BaseFlowStepComponent } from '../base-flow-step.component';

@Component({
  selector: 'app-flow-items-db',
  templateUrl: './flow-items-db.component.html',
  styleUrls: ['./flow-items-db.component.scss'],
  imports: [
    NgTemplateOutlet,
    PageFooterComponent,
    FxLayoutDirective,
    FxLayoutAlignDirective,
    FxFlexDirective,
    FxLayoutGapDirective,
    LaddaModule,
    CreateProgressComponent,
    MatIcon,
    CommonModule,
    FormsModule,
  ],
})
export class FlowItemsDbComponent extends BaseFlowStepComponent implements OnInit {
  activity = ButtonActivity;
  count = 0;
  updateCartInProgress = false;
  cart: DonationItem[] = [];
  sizeDescriptions = sizeDescriptions;

  constructor(
    override flowService: FlowService,
    override appService: AppService,
    override router: Router,
    override route: ActivatedRoute,
    override designService: DesignService,
    override responsiveService: ResponsiveService,
    protected dialog: MatDialog,
    private http: HttpClient,
    protected override optimizelyService: OptimizelyService,
    @Inject(ENVIRONMENT) protected override environment: Environment,
  ) {
    super(flowService, appService, router, route, designService, responsiveService, optimizelyService, environment);
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  static override isCompleted(lead: Lead) {
    return !lead.specification.isEmpty();
  }

  override isValid(): boolean {
    return !this.lead.specification.isEmpty();
  }

  override getFromValue(): Partial<Lead> {
    return {
      specification: this.flowService.lead.specification,
    };
  }

  override showError(): void {
    this.appService.createLeadActivity(this.lead?.id, ButtonActivity.DONATION_SPEC_SUBMIT_ERROR, undefined).pipe(take(1)).subscribe();
    this.dialog.open(InfoDialogComponent, {
      data: {
        title: 'No Items',
        message: 'You need to add at least one item to donate before proceeding to the next step',
        button: 'I understand.',
        icon: 'warning',
      },
    });
  }

  override saveButtonActivity(): ButtonActivity {
    return ButtonActivity.DONATION_SPEC_SUBMIT;
  }

  override pageVisitActivity(): PageActivity {
    return PageActivity.DONATION_SPEC_PAGE;
  }

  openAddItemPopup() {
    this.appService.createLeadActivity(this.lead?.id, ButtonActivity.DONATION_ITEM_SEARCH, undefined).pipe(take(1)).subscribe();
    this.dialog.open(ItemSearchDialogComponent, {
      width: '690px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'item-search-dialog',
    });
  }

  openInfoDialog(i: { item: DonationItem; count: number }) {
    this.dialog.open(InfoDialogComponent, {
      data: {
        title: i.item.name,
        message: i.item.description,
        button: 'Okay',
        icon: 'info',
      },
    });
  }

  handleQuantityChangeThroughInput(item: { item: DonationItem; count: number }, event: any) {
    const value = Number(event.target.value.replace(/[^0-9]/g, ''));
    event.target.value = value;

    item.count = value;
    this.updateCartInProgress = true;
    this.updateCartDebounced();
  }

  addItem(item: { item: DonationItem; count: number }) {
    item.count = item.count + 1;
    this.updateCartInProgress = true;
    this.updateCartDebounced();
  }

  addDisassembly() {
    if (this.lead && this.lead.specification) {
      this.lead.specification.disassembly += 1;
      this.updateCartInProgress = true;
      this.updateCartDebounced();
    }
  }

  removeItem(item: { item: DonationItem; count: number }) {
    if (item.count === 1) {
      this.dialog
        .open(ItemRemoveDialogComponent, {
          width: '550px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          panelClass: 'item-remove-dialog',
          data: item,
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) {
            this.lead.aggregatedItems = this.lead.aggregatedItems.filter(
              (i) => i.item.id !== item.item.id || i.item.type !== item.item.type,
            );
            if (!this.lead.aggregatedItems.length) {
              this.lead.specification.disassembly = 0;
            }
            this.flowService.updateCart(this.lead).subscribe();
            this.appService.createLeadActivity(this.lead?.id, ButtonActivity.ITEM_REMOVAL_SUCCESS, item.item.id).pipe(take(1)).subscribe();
          }
        });
      this.appService.createLeadActivity(this.lead?.id, ButtonActivity.ITEM_REMOVAL_POPUP, item.item.id).pipe(take(1)).subscribe();
    } else {
      item.count = item.count - 1;
      this.updateCartInProgress = true;
      this.updateCartDebounced();
    }
  }

  removeDisassembly() {
    if (this.lead && this.lead.specification && this.lead.specification.disassembly > 0) {
      this.lead.specification.disassembly -= 1;
      this.updateCartInProgress = true;
      this.updateCartDebounced();
    }
  }

  private updateCartDebounced = debounce(() => {
    this.flowService.updateCart(this.lead).subscribe({
      next: () => {
        this.updateCartInProgress = false;
      },
      error: () => {
        this.updateCartInProgress = false;
      },
    });
  }, 500);

  override saveLead(): void {
    super.saveLead();
  }
}
